import "./Home.css";
import FileDropzone from "../../components/FileDropzone/FileDropzone.js";
import ResultsClusters from "../../components/ResultsClusters/ResultsClusters.js";
import Toaster from "../../components/Toaster/Toaster.js";
import ExportButton from "../../components/ExportButton/ExportButton.js";

import { parseExcelFile } from "../../utils/parseExcel.js";
import { analyzeDuplicates } from "../../utils/dedupAlgorithm.js";

class Home {
  constructor() {
    this.fileDropzone = new FileDropzone();
    this.resultsClusters = new ResultsClusters();
    this.exportButton = new ExportButton();
    this.toaster = new Toaster();
  }

  mount(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Container not found: ${selector}`);
    }

    container.innerHTML = `
      <div class="home-page">
        <div class="page-content">
          <div class="page-header">
            <h2 class="page-title">Analyse des doublons</h2>
            <p class="page-description">
              Importez un fichier Excel contenant les mesures réflectométriques pour détecter automatiquement les doublons.
            </p>
          </div>
          
          <div id="file-dropzone-mount" class="dropzone-container">
            <!-- FileDropzone sera monté ici -->
          </div>

          <div id="results-mount" class="results-container" style="display: none;">
            <!-- Résultats seront affichés ici -->
          </div>
          
          <div id="toaster-mount">
            <!-- Toaster sera monté ici -->
          </div>

          <div id="export-button-mount">
            <!-- Export button sera monté ici -->
          </div>

        </div>
      </div>
    `;

    // Montage du FileDropzone
    this.fileDropzone.mount("#file-dropzone-mount");
    this.fileDropzone.setFileSelectHandler(this.handleFileSelect.bind(this));
    this.fileDropzone.setErrorHandler(this.handleFileError.bind(this));

    // Montage du toaster
    this.toaster.mount("#toaster-mount");

    // Montage du export button (initialement caché)
    this.exportButton.mount("#export-button-mount");
  }

  unmount() {
    // Démontage des composants
    this.fileDropzone.unmount();
    this.resultsClusters.unmount();
    this.exportButton.unmount();
    this.toaster.unmount();
  }

  async handleFileSelect(file) {
    // Notification de début de traitement
    this.toaster.showInfo(`Traitement du fichier: ${file.name}`);

    this.clearResults();
    this.fileDropzone.setProcessing(true);

    try {
      const parseResult = await parseExcelFile(file);

      if (parseResult.success) {
        // DEBUG: Vérifier contenu parseResult
        console.log("parseResult complet:", parseResult);
        console.log("Nombre de fibres:", parseResult.data.fibers.length);
        console.log("Première fibre:", parseResult.data.fibers[0]);

        // Notification de succès du parsing
        const warningCount = parseResult.warnings?.missingWavelengths?.length || 0;
        let message = `Fichier analysé avec succès: ${parseResult.data.validRows} fibres détectées`;

        if (warningCount > 0) {
          message += ` (${warningCount} avertissement${warningCount > 1 ? "s" : ""} détecté${warningCount > 1 ? "s" : ""})`;
        }

        this.toaster.showSuccess(message);

        // Analyse des doublons
        this.processParsedData(parseResult);
      } else {
        console.error("Erreur de parsing:", parseResult);
        this.handleParsingError(parseResult);
      }
    } catch (error) {
      console.error("Erreur inattendue:", error);
      this.handleFileError("Erreur inattendue lors du traitement");
    } finally {
      this.fileDropzone.setProcessing(false);
    }
  }

  processParsedData(parseResult) {
    // Notification de début d'analyse
    this.toaster.showInfo("Démarrage de l'analyse des doublons...");

    // Analyse des doublons avec le nouvel algorithme
    const analysisResult = analyzeDuplicates(parseResult.data.fibers);

    console.log("analysisResult:", analysisResult);
    if (analysisResult.clusters && analysisResult.clusters.length > 0) {
      console.log("Premier cluster:", analysisResult.clusters[0]);
    }

    // Notification du résultat de l'analyse
    const hasWarnings = parseResult.warnings?.missingWavelengths?.length > 0;
    const hasAlerts = analysisResult.clustersFound > 0;

    if (hasWarnings && hasAlerts) {
      this.toaster.showWarning(
        `Analyse terminée: ${parseResult.warnings.missingWavelengths.length} avertissement(s) et ${analysisResult.clustersFound} alerte(s) détectée(s)`,
        { duration: 8000 }
      );
    } else if (hasWarnings) {
      this.toaster.showWarning(
        `Analyse terminée: ${parseResult.warnings.missingWavelengths.length} avertissement(s) détecté(s)`,
        { duration: 7000 }
      );
    } else if (hasAlerts) {
      this.toaster.showWarning(
        `Analyse terminée: ${analysisResult.clustersFound} alerte(s) de doublons potentiels détectée(s)`,
        {
          duration: 7000,
        }
      );
    } else {
      this.toaster.showSuccess("Analyse terminée: aucun problème détecté");
    }

    this.displayResults({
      warnings: parseResult.warnings,
      analysisResult: analysisResult,
    });
  }

  displayResults(data) {
    this.resultsClusters.setData(data);
    this.resultsClusters.mount("#results-mount");
    // Set export data and show export button
    this.exportButton.setExportData(data);
    this.exportButton.show();

    this.showResults();
  }

  clearResults() {
    this.resultsClusters.clear();
    if (this.exportButton) {
      this.exportButton.hide();
    }
    this.hideResults();
  }

  handleParsingError(result) {
    // Toutes les erreurs sont maintenant des erreurs de toaster (pas de modal bloquante)
    this.handleFileError(result.error?.message || result.message || "Erreur lors de l'analyse du fichier");
  }

  handleFileError(message) {
    console.error("Erreur fichier:", message);
    this.toaster.showError(message, { duration: 8000 });
  }

  showResults() {
    const resultsContainer = document.getElementById("results-mount");
    if (resultsContainer) {
      resultsContainer.style.display = "block";
    }
  }

  hideResults() {
    const resultsContainer = document.getElementById("results-mount");
    if (resultsContainer) {
      resultsContainer.style.display = "none";
    }
  }
}

export default Home;
